import React, { useState, useMemo } from 'react';
import useDashboardData from '../../hooks/useDashboardData';
import AnalyticsCards from '../../components/AnalyticsCards/AnalyticsCards';
import FilterBar from '../../components/FilterBar/FilterBar';
import CallList from '../../components/CallList/CallList';
import CallDetail from '../../components/CallDetail/CallDetail';
import CallbackQueue from '../../components/CallbackQueue/CallbackQueue';
import SubmissionsList from '../../components/SubmissionsList/SubmissionsList';
import FeedbackTab from '../../components/FeedbackTab/FeedbackTab';
import BankSurveyTab from '../../components/BankSurveyTab/BankSurveyTab';
import ImpactSurveyTab from '../../components/ImpactSurveyTab/ImpactSurveyTab';
import SermonFeedbackTab from '../../components/SermonFeedbackTab/SermonFeedbackTab';
import CampaignSurveyTab from '../../components/CampaignSurveyTab/CampaignSurveyTab';
import YoungProfRsvpTab from '../../components/YoungProfRsvpTab/YoungProfRsvpTab';
import HackathonSurveyTab from '../../components/HackathonSurveyTab/HackathonSurveyTab';
import TakeoffRsvpTab from '../../components/TakeoffRsvpTab/TakeoffRsvpTab';
import NitaSurveyTab from '../../components/NitaSurveyTab/NitaSurveyTab';
import AudioFiles from '../../components/AudioFiles/AudioFiles';
import { deleteCall, deleteCallback, bulkDeleteCalls, bulkDeleteCallbacks } from '../../api/client';

export default function Dashboard() {
  const [filters, setFilters] = useState({});
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [activeView, setActiveView] = useState('dashboard');

  const stableFilters = useMemo(() => filters, [
    filters.sentiment,
    filters.callback_detected,
  ]);

  const { calls, callbacks, analytics, refreshAll } = useDashboardData(stableFilters);

  const handleDeleteCall = async (callId) => {
    await deleteCall(callId);
    refreshAll();
  };

  const handleDeleteCallback = async (callbackId) => {
    await deleteCallback(callbackId);
    refreshAll();
  };

  const handleBulkDeleteCalls = async (callIds) => {
    await bulkDeleteCalls(callIds);
    refreshAll();
  };

  const handleBulkDeleteCallbacks = async (callbackIds) => {
    await bulkDeleteCallbacks(callbackIds);
    refreshAll();
  };

  return (
    <div>
      <div className="nav-tabs">
        <button
          className={`nav-tab${activeView === 'dashboard' ? ' nav-tab--active' : ''}`}
          onClick={() => setActiveView('dashboard')}
        >
          Dashboard
        </button>
        <button
          className={`nav-tab${activeView === 'submissions' ? ' nav-tab--active' : ''}`}
          onClick={() => setActiveView('submissions')}
        >
          Submissions
        </button>
        <button
          className={`nav-tab${activeView === 'feedback' ? ' nav-tab--active' : ''}`}
          onClick={() => setActiveView('feedback')}
        >
          Feedback
        </button>
        <button
          className={`nav-tab${activeView === 'bank-survey' ? ' nav-tab--active' : ''}`}
          onClick={() => setActiveView('bank-survey')}
        >
          Bank Survey
        </button>
        <button
          className={`nav-tab${activeView === 'impact-survey' ? ' nav-tab--active' : ''}`}
          onClick={() => setActiveView('impact-survey')}
        >
          IMPACT Survey
        </button>
        <button
          className={`nav-tab${activeView === 'sermon-feedback' ? ' nav-tab--active' : ''}`}
          onClick={() => setActiveView('sermon-feedback')}
        >
          Sermon Feedback
        </button>
        <button
          className={`nav-tab${activeView === 'campaign-survey' ? ' nav-tab--active' : ''}`}
          onClick={() => setActiveView('campaign-survey')}
        >
          Campaign Survey
        </button>
        <button
          className={`nav-tab${activeView === 'young-prof-rsvp' ? ' nav-tab--active' : ''}`}
          onClick={() => setActiveView('young-prof-rsvp')}
        >
          Young Prof RSVPs
        </button>
        <button
          className={`nav-tab${activeView === 'hackathon-survey' ? ' nav-tab--active' : ''}`}
          onClick={() => setActiveView('hackathon-survey')}
        >
          Hackathon Survey
        </button>
        <button
          className={`nav-tab${activeView === 'takeoff-rsvp' ? ' nav-tab--active' : ''}`}
          onClick={() => setActiveView('takeoff-rsvp')}
        >
          Takeoff RSVP
        </button>
        <button
          className={`nav-tab${activeView === 'nita-survey' ? ' nav-tab--active' : ''}`}
          onClick={() => setActiveView('nita-survey')}
        >
          NITA Survey
        </button>
        <button
          className={`nav-tab${activeView === 'audio' ? ' nav-tab--active' : ''}`}
          onClick={() => setActiveView('audio')}
        >
          Audio
        </button>
      </div>

      {activeView === 'audio' ? (
        <AudioFiles />
      ) : activeView === 'nita-survey' ? (
        <NitaSurveyTab />
      ) : activeView === 'takeoff-rsvp' ? (
        <TakeoffRsvpTab />
      ) : activeView === 'hackathon-survey' ? (
        <HackathonSurveyTab />
      ) : activeView === 'young-prof-rsvp' ? (
        <YoungProfRsvpTab />
      ) : activeView === 'campaign-survey' ? (
        <CampaignSurveyTab />
      ) : activeView === 'sermon-feedback' ? (
        <SermonFeedbackTab />
      ) : activeView === 'impact-survey' ? (
        <ImpactSurveyTab />
      ) : activeView === 'bank-survey' ? (
        <BankSurveyTab />
      ) : activeView === 'feedback' ? (
        <FeedbackTab />
      ) : activeView === 'submissions' ? (
        <SubmissionsList />
      ) : (
        <>
          {analytics.error && (
            <div className="error-message">
              Failed to load data: {analytics.error}. Is the backend running?
            </div>
          )}

          <AnalyticsCards analytics={analytics.data?.data} />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '1rem 0' }}>
            <FilterBar filters={filters} onFilterChange={setFilters} />
            <button className="btn-primary" onClick={refreshAll}>
              Refresh
            </button>
          </div>

          <div className="grid grid-2">
            <div>
              {calls.loading && !calls.data ? (
                <div className="loading">Loading calls...</div>
              ) : (
                <CallList
                  calls={calls.data?.data || []}
                  onSelectCall={setSelectedCallId}
                  onDeleteCall={handleDeleteCall}
                  onBulkDeleteCalls={handleBulkDeleteCalls}
                />
              )}
            </div>

            <div>
              {callbacks.loading && !callbacks.data ? (
                <div className="loading">Loading callbacks...</div>
              ) : (
                <CallbackQueue callbacks={callbacks.data?.data || []} onDeleteCallback={handleDeleteCallback} onBulkDeleteCallbacks={handleBulkDeleteCallbacks} />
              )}
            </div>
          </div>

          {selectedCallId && (
            <CallDetail
              callId={selectedCallId}
              onClose={() => setSelectedCallId(null)}
            />
          )}
        </>
      )}
    </div>
  );
}
